import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  Observable,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { LocalStorage } from "./LocalStorage";
import {
  appSessionStillUsable,
  handleSessionExpired,
  refreshTokenIfNeeded,
} from "../reduxStore/auth/thunks";
import { trackException } from "./amplitudeAnalytics";

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_GRAPHQL_API_URI,
});

const authLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    let subscriber: { unsubscribe: () => void } | undefined;

    refreshTokenIfNeeded()
      .then((refreshed) => {
        if (!refreshed) {
          observer.error(new Error("Session expired"));
          return;
        }

        console.log("~~~REFRESHING TOKEN");
        const tokenExpirationTime = LocalStorage.getTokenExpirationTime();
        const date = new Date(Number(tokenExpirationTime) * 1000);
        console.log("Current Section Expires at: ", date.toISOString());

        const token = LocalStorage.getToken();
        operation.setContext({
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        subscriber = forward(operation).subscribe({
          next: (result) => {
            observer.next(result);
          },
          error: (error) => {
            observer.error(error);
          },
          complete: () => {
            observer.complete();
          },
        });
      })
      .catch((error) => {
        observer.error(error);
      });

    return () => {
      subscriber?.unsubscribe();
    };
  });
});

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        if (
          err.extensions?.code === "UNAUTHENTICATED" ||
          err.extensions?.code === "invalid-jwt"
        ) {
          console.log("~~~UNAUTHENTICATED or invalid-jwt");
          return new Observable((observer) => {
            let subscriber: { unsubscribe: () => void } | undefined;

            refreshTokenIfNeeded(true)
              .then((refreshed) => {
                if (!refreshed) {
                  observer.error(err);
                  return;
                }

                const token = LocalStorage.getToken();
                operation.setContext(({ headers = {} }) => ({
                  headers: {
                    ...headers,
                    Authorization: `Bearer ${token}`,
                  },
                }));

                subscriber = forward(operation).subscribe({
                  next: (result) => {
                    observer.next(result);
                  },
                  error: (error) => {
                    observer.error(error);
                  },
                  complete: () => {
                    observer.complete();
                  },
                });
              })
              .catch((error) => {
                observer.error(error);
              });

            return () => {
              subscriber?.unsubscribe();
            };
          });
        } else {
          trackException(err, {
            source: "apollo.graphql",
            operation: operation.operationName,
            code: err.extensions?.code,
          });
        }
      }
    }

    if (networkError) {
      console.log(`[Network error]: ${networkError}`);
      const statusCode = (networkError as { statusCode?: number }).statusCode;
      if (statusCode === 401 && !appSessionStillUsable()) {
        handleSessionExpired().then();
        return;
      }
      trackException(networkError, {
        source: "apollo.network",
        operation: operation.operationName,
      });
    }
  },
);

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(httpLink)]),
  cache: new InMemoryCache(),
});

export default client;
